import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

export type ClienteReportRow = {
  id: string;
  slug: string;
  title: string;
  palavra_chave: string | null;
  cidade: string | null;
  bairro: string | null;
  matrix: { nome: string | null } | null;
  gsc_position: number | null;
  gsc_previous_position: number | null;
  gsc_impressions: number | null;
  gsc_clicks: number | null;
  gsc_updated_at: string | null;
  published: boolean;
};

export type ClienteReport = {
  label: string;
  rows: ClienteReportRow[];
};

/**
 * Public read-only report accessed via a share-link token.
 * Validates the token, increments access count, and returns the published pages.
 */
export const getReportByToken = createServerFn({ method: "GET" })
  .inputValidator((input) => z.object({ token: z.string().min(8) }).parse(input))
  .handler(async ({ data }): Promise<ClienteReport> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: link, error: linkError } = await supabaseAdmin
      .from("report_share_links")
      .select("id, label, revoked, access_count")
      .eq("token", data.token)
      .maybeSingle();

    if (linkError) throw new Error(linkError.message);
    if (!link || link.revoked) {
      throw new Error("Link inválido ou revogado");
    }

    // Fire-and-forget access tracking
    await supabaseAdmin
      .from("report_share_links")
      .update({
        access_count: (link.access_count ?? 0) + 1,
        last_accessed_at: new Date().toISOString(),
      })
      .eq("id", link.id);

    const { data: rows, error } = await supabaseAdmin
      .from("landing_pages")
      .select(
        "id, slug, title, palavra_chave, cidade, bairro, gsc_position, gsc_previous_position, gsc_impressions, gsc_clicks, gsc_updated_at, published, matrix:keyword_matrices(nome)",
      )
      .eq("published", true)
      .order("gsc_position", { ascending: true, nullsFirst: false });

    if (error) throw new Error(error.message);

    return {
      label: link.label ?? "Relatório de Landing Pages",
      rows: (rows ?? []) as unknown as ClienteReportRow[],
    };
  });